import { useCallback, useEffect, useState } from "react";
import { Cpu, Radio, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DEVICE_REGISTRY_KEY,
  listOnlineDevices,
  type DeviceRecord,
} from "@/lib/deviceRegistryStore";
import { getProject } from "@/lib/prometheusProjectsStore";

export type MonitorPanelProps = {
  /** Click a live device → focus its owner project (sets ?project= + projects section). */
  onSelectProject: (projectId: string) => void;
  className?: string;
};

function kindLabel(kind: DeviceRecord["kind"]): string {
  switch (kind) {
    case "esp32":
      return "ESP32";
    case "arduino":
      return "Arduino";
    case "camera":
      return "Cámara";
    default:
      return "Otro";
  }
}

function transportLabel(t: DeviceRecord["transport"]): string {
  if (!t) return "—";
  return t.toUpperCase();
}

export function MonitorPanel({ onSelectProject, className }: MonitorPanelProps) {
  const [devices, setDevices] = useState<DeviceRecord[]>([]);

  const reload = useCallback(() => {
    setDevices(listOnlineDevices());
  }, []);

  useEffect(() => {
    reload();
    const onStorage = (e: StorageEvent) => {
      if (e.key === DEVICE_REGISTRY_KEY) reload();
    };
    const onLocal = () => reload();
    window.addEventListener("storage", onStorage);
    window.addEventListener("prometheus-device-registry", onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("prometheus-device-registry", onLocal);
    };
  }, [reload]);

  if (devices.length === 0) {
    return (
      <div
        className={cn(
          "flex h-full items-center justify-center p-6 bg-void",
          className
        )}
      >
        <div className="max-w-md w-full rounded-xl border border-dashed border-border bg-card/40 px-6 py-10 text-center">
          <WifiOff className="w-7 h-7 text-muted-foreground mx-auto mb-3" />
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.18em] text-primary font-bold mb-2">
            Monitor
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            No hay dispositivos en vivo. Conéctalos desde el Wiring del proyecto.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex h-full flex-col min-h-0 bg-void", className)}>
      <header className="shrink-0 border-b border-border/60 px-4 py-3 pl-14 md:pl-4">
        <p className="font-mono-tech text-[10px] uppercase tracking-[0.18em] text-primary font-bold">
          Monitor
        </p>
        <p className="text-[12px] text-muted-foreground mt-0.5">
          Dispositivos en vivo ({devices.length})
        </p>
      </header>
      <ul className="flex-1 overflow-y-auto p-3 space-y-2">
        {devices.map((d) => {
          const project = getProject(d.projectId);
          const title = project?.title ?? d.projectId;
          return (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => onSelectProject(d.projectId)}
                className={cn(
                  "w-full flex items-start gap-3 rounded-xl border border-border/60 bg-card/40 px-3 py-3 text-left",
                  "hover:border-primary/40 hover:bg-primary/5 transition-colors cursor-pointer"
                )}
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                  <Cpu className="w-4 h-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-medium text-foreground truncate">
                    {d.alias}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground font-mono-tech">
                    <span>{kindLabel(d.kind)}</span>
                    <span className="opacity-40">·</span>
                    <span className="inline-flex items-center gap-1">
                      <Radio className="w-3 h-3" />
                      {transportLabel(d.transport)}
                    </span>
                  </span>
                  <span className="mt-1.5 block text-[11px] text-foreground/80 line-clamp-2">
                    {title}
                  </span>
                </span>
                <span className="shrink-0 mt-1 h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.7)]" aria-label="online" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
